import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'js/ui/legacy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import actions, { selectors } from 'js/redux/reducers/FormulaSupport';
import { selectors as cacheSelectors } from 'js/redux/reducers/Cache';
import { selectors as configSelectors } from 'js/redux/reducers/Configuration';
import {
  components,
  formatFormulaAmount,
  formulaBase,
  formulaProblem,
  supportContext,
} from 'js/mylib/FormulaSupport';
import { confirmAlert } from 'js/ext/react-confirm-alert';
import { offline_mode } from 'js/Constants';
import ColorThumbnail from '../shared/ColorThumbnail';
import '../../../css/formula-support.scss';

const unseen = (item) =>
  item.messages.some((message) => message.sender === 'LAB' && !message.seen_at);
const date = (value) =>
  value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString()
    : '—';

export default function FormulaSupportQueue({ active }) {
  const { t } = useTranslation('translations');
  const dispatch = useDispatch();
  const state = useSelector(selectors.state);
  const products = useSelector(cacheSelectors.products);
  const cntmap = useSelector(cacheSelectors.cntmap);
  const cntcodemap = useSelector(cacheSelectors.cntcodemap);
  const zones = useSelector(cacheSelectors.zones);
  const zone = useSelector(configSelectors.zone);
  const config = useSelector(configSelectors.config_values);
  const shotFormatter = useSelector(configSelectors.shotFormatter);
  const routeSearch = useSelector((store) => store.router.location.search);
  const order = useSelector((store) => store.order);
  const privileges = useSelector((store) => store.user.current_user.privileges);
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [ascending, setAscending] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [checked, setChecked] = useState([]);
  const attempted = useRef(new Set());
  const openedRoute = useRef(null);
  const allCheckbox = useRef(null);
  const listScroll = useRef(null);
  const requestedId =
    Number(new URLSearchParams(routeSearch).get('supportCase')) || null;
  const feedbackOrder =
    Number(new URLSearchParams(routeSearch).get('feedbackOrder')) || null;
  const linkedCase =
    feedbackOrder === state.lookupOrderId &&
    state.orderFeedbackCase?.supportcaseid === requestedId
      ? state.orderFeedbackCase
      : null;

  useEffect(() => {
    if (active && requestedId && feedbackOrder) {
      dispatch(actions.lookupOrder(feedbackOrder));
    }
  }, [active, requestedId, feedbackOrder, dispatch]);

  useEffect(() => {
    if (requestedId && requestedId !== openedRoute.current) {
      setSelectedId(requestedId);
      const requested =
        linkedCase ||
        state.cases.find((item) => item.supportcaseid === requestedId);
      if (requested) {
        setArchived(requested.status === 'ARCHIVED');
        openedRoute.current = requestedId;
      }
    }
  }, [requestedId, state.cases, linkedCase]);

  const typeLabel = (item) =>
    item?.category === 'formula_request'
      ? t('lbl.newFormulaRequest', 'New formula request')
      : t('lbl.formulaFeedback', 'Formula feedback');
  const rows =
    linkedCase &&
    (linkedCase.status === 'ARCHIVED') === archived &&
    !state.cases.some((item) => item.supportcaseid === linkedCase.supportcaseid)
      ? [linkedCase, ...state.cases]
      : state.cases;

  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => {
      dispatch(
        actions.fetch({
          search,
          archived,
          page: 1,
          page_size: state.pageSize,
          sort: ascending ? 'asc' : 'desc',
        })
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [active, archived, ascending, dispatch, search, state.pageSize]);
  const loadMore = useCallback(() => {
    if (
      !active ||
      state.loading ||
      !state.hasNext ||
      (state.query.search || '') !== search ||
      state.query.archived !== archived ||
      state.query.sort !== (ascending ? 'asc' : 'desc') ||
      state.query.page !== state.page
    )
      return;
    dispatch(actions.fetch({ ...state.query, page: state.page + 1 }));
  }, [active, archived, ascending, dispatch, search, state]);
  useEffect(() => {
    const list = listScroll.current;
    if (
      list &&
      list.clientHeight &&
      list.scrollHeight <= list.clientHeight + 80
    )
      loadMore();
  }, [loadMore, state.cases]);
  const selected =
    rows.find((item) => item.supportcaseid === selectedId) || linkedCase;
  const { context, original, response, received } = supportContext(selected);

  useEffect(() => {
    if (!active || !selected || !unseen(selected)) return;
    const key = `${selected.supportcaseid}:${selected.messages
      .filter((m) => m.sender === 'LAB' && !m.seen_at)
      .map((m) => m.formulasupportmessageid)
      .join(',')}`;
    if (attempted.current.has(key)) return;
    attempted.current.add(key);
    dispatch(actions.markSeen(selected.supportcaseid));
  }, [active, selected, dispatch]);

  useEffect(() => {
    setChecked((ids) =>
      ids.filter((id) =>
        state.cases.some(
          (item) => item.supportcaseid === id && item.status !== 'ARCHIVED'
        )
      )
    );
  }, [state.cases]);
  const visibleChecked = rows.filter((item) =>
    checked.includes(item.supportcaseid)
  );
  useEffect(() => {
    if (allCheckbox.current)
      allCheckbox.current.indeterminate =
        visibleChecked.length > 0 && visibleChecked.length < rows.length;
  }, [visibleChecked.length, rows.length]);

  const formulaRows = (formula, productid) => {
    const product = products.find((item) => item.productid === productid);
    const base = formulaBase(formula, product);
    return (
      <>
        <dt>{t('lbl.baseCode', 'Base code')}</dt>
        <dd>{formula?.basecode || '—'}</dd>
        <dt>{t('lbl.formula', 'Formula')}</dt>
        <dd>
          <div className="support-components">
            {components(formula).map((cnt, index) => {
              const matches = [...cntmap.values()].filter(
                (entry) => entry.cntcode === cnt.cntcode
              );
              const known =
                cntmap.get(cnt.cntid) ||
                (matches.length === 1 ? matches[0] : null);
              return (
                <div key={`${cnt.cntid}-${index}`}>
                  <ColorThumbnail
                    rgb={Number.isInteger(cnt.rgb) ? cnt.rgb : known?.rgb}
                    size="1.25rem"
                  />
                  <span>
                    {cnt.cntcode || known?.cntcode || '—'}
                    {!config.hide_formula_volumes && Number.isFinite(cnt.volume)
                      ? ` · ${formatFormulaAmount(
                          shotFormatter,
                          cnt.volume,
                          known?.specificgravity
                        )}`
                      : ''}
                  </span>
                </div>
              );
            })}
            {!components(formula).length && '—'}
          </div>
        </dd>
      </>
    );
  };
  const responseZoneAvailable =
    Number.isInteger(response?.zoneid) &&
    zones.some((availableZone) => availableZone.zoneid === response.zoneid);
  const problem =
    response?.zoneid === zone?.zoneid
      ? formulaProblem(response, products, cntcodemap)
      : null;
  const unavailable = !selected
    ? t('lbl.selectFormulaSupportCase', 'Select a formula support case')
    : selected.status === 'ARCHIVED'
      ? t('msg.supportArchived', 'Archived cases cannot be used')
      : !privileges.includes('order_local_formula') || offline_mode
        ? t(
            'msg.supportOrderUnavailable',
            'Custom formula orders are unavailable'
          )
        : !response
          ? '  ' // Awaiting response
          : !responseZoneAvailable
            ? t(
                'msg.supportZoneUnavailable',
                'The formula zone is unavailable.'
              )
            : problem
              ? t(
                  `msg.supportFormula.${problem}`,
                  {
                    incomplete:
                      'The response needs a complete formula snapshot.',
                    catalogue: 'The product, base or can is unavailable.',
                    can: 'The can size or fill no longer matches the response.',
                    components:
                      'The formula contains invalid or unavailable colorants.',
                  }[problem]
                )
              : '';
  const useFormula = () => {
    const run = () => dispatch(actions.use(selected.supportcaseid));
    if (order.product || order.color || order.item?.itemid) {
      confirmAlert({
        title: t('lbl.useFormula', 'Use formula'),
        message: t(
          'msg.replaceCurrentOrder',
          'Replace the current order with this formula?'
        ),
        buttons: [
          { label: t('fn.cancel', 'Cancel') },
          { label: t('fn.continue', 'Continue'), onClick: run },
        ],
      });
    } else run();
  };

  return (
    <div className="formula-support-queue" data-testid="formula-support-queue">
      <section
        className="support-list"
        aria-label={t('lbl.formulaSupport', 'Formula Support')}
      >
        <div className="support-toolbar">
          <label className="support-search">
            <FontAwesomeIcon icon="search" />
            <input
              aria-label={t('prompt.search', 'Search')}
              placeholder={t('prompt.search', 'Search')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setChecked([]);
                if (listScroll.current) listScroll.current.scrollTop = 0;
              }}
            />
          </label>
          <select
            aria-label={t('lbl.status', 'Status')}
            value={archived ? 'archived' : 'active'}
            onChange={(e) => {
              setArchived(e.target.value === 'archived');
              setSelectedId(null);
              setChecked([]);
              if (listScroll.current) listScroll.current.scrollTop = 0;
            }}
          >
            <option value="active">{t('lbl.active', 'Active')}</option>
            <option value="archived">{t('lbl.archived', 'Archived')}</option>
          </select>
        </div>
        {state.loading && <p role="status">{t('lbl.loading', 'Loading…')}</p>}
        {!!state.error && (
          <div role="alert" className="support-error">
            {t(
              'msg.supportError',
              'The support operation failed. Please try again.'
            )}{' '}
            <button
              type="button"
              onClick={() => {
                attempted.current.clear();
                dispatch(actions.fetch());
              }}
            >
              {t('fn.retry', 'Retry')}
            </button>
          </div>
        )}
        <div
          className="support-table-scroll scroll"
          ref={listScroll}
          tabIndex={0}
          onScroll={(event) => {
            const list = event.currentTarget;
            if (list.scrollHeight - list.scrollTop - list.clientHeight < 80)
              loadMore();
          }}
        >
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    ref={allCheckbox}
                    type="checkbox"
                    aria-label={t('fn.selectAll', 'Select all')}
                    disabled={archived || !rows.length || state.pending}
                    checked={
                      !!rows.length && visibleChecked.length === rows.length
                    }
                    onChange={(e) =>
                      setChecked(
                        e.target.checked
                          ? [
                              ...new Set([
                                ...checked,
                                ...rows.map((item) => item.supportcaseid),
                              ]),
                            ]
                          : checked.filter(
                              (id) =>
                                !rows.some((item) => item.supportcaseid === id)
                            )
                      )
                    }
                  />
                </th>
                <th>{t('lbl.requestId', 'Request ID')}</th>
                <th>{t('lbl.color', 'Color')}</th>
                <th>{t('lbl.product', 'Product')}</th>
                <th aria-sort={ascending ? 'ascending' : 'descending'}>
                  <button
                    type="button"
                    onClick={() => {
                      setAscending(!ascending);
                      if (listScroll.current) listScroll.current.scrollTop = 0;
                    }}
                  >
                    {t('lbl.received', 'Received')} {ascending ? '▴' : '▾'}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const data = supportContext(item);
                const status = unseen(item)
                  ? t('lbl.unseen', 'Unseen response')
                  : data.response
                    ? t('lbl.ready', 'Ready')
                    : t('msg.supportAwaiting', 'Awaiting response');
                return (
                  <tr
                    key={item.supportcaseid}
                    className={
                      selectedId === item.supportcaseid ? 'selected' : ''
                    }
                    data-testid={`support-case-${item.supportcaseid}`}
                    onClick={() => setSelectedId(item.supportcaseid)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`${t('fn.select', 'Select')} ${item.supportcaseid}`}
                        disabled={archived || state.pending}
                        checked={checked.includes(item.supportcaseid)}
                        onChange={(e) =>
                          setChecked(
                            e.target.checked
                              ? [...checked, item.supportcaseid]
                              : checked.filter(
                                  (id) => id !== item.supportcaseid
                                )
                          )
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        aria-label={`${item.supportcaseid}: ${status}`}
                        onClick={() => setSelectedId(item.supportcaseid)}
                      >
                        <span className="support-state" title={status}>
                          <FontAwesomeIcon
                            icon={
                              unseen(item)
                                ? 'envelope'
                                : data.response
                                  ? 'check'
                                  : 'clock-rotate-left'
                            }
                          />
                        </span>
                        #{item.supportcaseid}
                      </button>
                    </td>
                    <td>
                      <div className="support-color">
                        <ColorThumbnail
                          rgb={data.context.rgb ?? data.context.formula?.rgb}
                          size="2rem"
                        />
                        <span>
                          {data.context.colourcode || '—'}
                          <br />
                          {data.context.colourname}
                        </span>
                      </div>
                    </td>
                    <td>{data.context.productname || '—'}</td>
                    <td>{date(data.received)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && !state.loading && (
            <p className="support-empty">
              {t('lbl.noSupportCases', 'No support cases')}
            </p>
          )}
        </div>
        <div className="support-list-actions">
          <Button
            disabled={!visibleChecked.length || state.pending || archived}
            onClick={() =>
              dispatch(
                actions.archive(
                  visibleChecked.map((item) => item.supportcaseid)
                )
              )
            }
            style={{ width: '200px', marginBottom: '0' }}
          >
            {t('fn.archive', 'Archive')}
          </Button>
        </div>
      </section>
      <aside className="support-response">
        <h5>
          {typeLabel(selected)} #{selected?.supportcaseid}
          <span className="support-status">
            {!response
              ? t('msg.supportAwaiting', 'Awaiting response')
              : t(`lbl.supportStatus.${selected.status}`, selected.status)}
          </span>
        </h5>
        {state.seeing.includes(selectedId) && (
          <p role="status">
            {t('msg.supportMarkingSeen', 'Marking response as seen…')}
          </p>
        )}
        {state.pending && <p role="status">{t('lbl.loading', 'Loading…')}</p>}
        <div className="support-response-scroll scroll">
          {selected ? (
            <dl>
              <dt>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                  }}
                >
                  {t('lbl.color', 'Color')}{' '}
                  <ColorThumbnail
                    rgb={context.rgb ?? context.formula?.rgb}
                    size="1.5rem"
                  />
                </div>
              </dt>
              <dd>
                {context.colourcode || '—'}
                <br />
                {context.colourname}
              </dd>
              <dt>{t('lbl.product', 'Product')}</dt>
              <dd>{context.productname || '—'}</dd>
              {formulaRows(response?.formula, context.productid)}
              <dt>{t('lbl.notes', 'Notes')}</dt>
              <dd>{original?.notes || '—'}</dd>
              <dt>{t('lbl.creatorComment', 'Comment')}</dt>
              <dd>{response?.notes || '—'}</dd>
              <dt>{t('lbl.received', 'Received')}</dt>
              <dd>{date(received)}</dd>
            </dl>
          ) : (
            <p>
              {t(
                'lbl.selectFormulaSupportCase',
                'Select a formula support case'
              )}
            </p>
          )}
        </div>
        <div className="support-response-actions">
          {unavailable && selected ? (
            <small id="support-use-reason">{unavailable}</small>
          ) : (
            <Button
              color="primary"
              aria-describedby="support-use-reason"
              disabled={!!unavailable || state.pending}
              onClick={useFormula}
            >
              {t('fn.useFormula', 'Use formula')}
            </Button>
          )}
        </div>
      </aside>
    </div>
  );
}
FormulaSupportQueue.propTypes = { active: PropTypes.bool.isRequired };
